import { SMSPointOfService } from '@dh-io-emh/commerce-api/src/models/shared/ocb';
import { ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import { SeamlessConnection, SeamlessCoreStore } from '@seamless/store';
import { AnyAction, Dispatch } from 'redux';
import {
  Configuration,
  GetEnrichedDataForStockVehiclesApi as EnrichmentApi,
  type EquipmentCategory,
} from '../../api/enrichment/client';
import { adjustEnrichmentEquipments, type EquipmentCategories, getLines, Lines } from '../../api/enrichment/helpers';
import { infuse } from '../../ioc-config';
import type { IProductFilterPaginationDTO } from '../../models/product-filter/product-filter-base.model';
import Vehicle from '../../models/vehicle.model';
import { Logger } from '../../utils/logger.util';
import { initializeStoreConnection, usePersistentStore } from '../store.util';

export type Vendor = { pointOfServices: SMSPointOfService[] };

export enum ErrorType {
  NotFound = 'NOT_FOUND',
}

export type ProductState = {
  compatibility: Record<string, any>;
  entity: ProductDetail;
  error?: ErrorType;
  loadingProduct?: string;
  popularProducts: any[];
  vendor: Vendor;
  equipmentCategories: EquipmentCategories;
  equipmentLoaded: boolean;
  lines?: Lines;
};

export type PublicProductDispatchers = {
  setEquipmentLoaded: (enable: boolean) => void;
  setEquipments: (equipments: EquipmentCategory[]) => void;
  setError: (error?: ErrorType) => void;
  setLoadingProducts: (code?: string) => void;
  setPopularProducts: (products: Vehicle[]) => void;
  setProduct: (product: ProductDetail) => void;
  setVendor: (vendor: Vendor) => void;
};

export type AsyncProductDispatchers = {
  loadPopularProducts: ({ category, fallbackCategory, filters, pageSize, sort }: any) => Promise<void>;
  loadProduct: (productCode?: string) => Promise<void>;
  loadEnrichmentEquipments: (commissionNumber: string) => Promise<void>;
};

export type ProductDispatchers = PublicProductDispatchers & AsyncProductDispatchers;

export const connectionName = 'EMH_PRODUCT' as const;

export const initialStateParser = (state: ProductState): ProductState => ({
  ...state,
  error: undefined,
  entity: state.entity,
  loadingProduct: undefined,
});

export const { initialState, updateState } = usePersistentStore<ProductState>(
  connectionName,
  {
    compatibility: {},
    entity: {} as ProductDetail,
    error: undefined,
    loadingProduct: undefined,
    popularProducts: [],
    vendor: { pointOfServices: [] },
    equipmentCategories: { highlights: [], details: [] },
    equipmentLoaded: false,
    lines: undefined,
  },
  initialStateParser,
);

const SET_ERROR = 'SET_ERROR';
const SET_LOADING_PRODUCT = 'SET_LOADING_PRODUCT';
const SET_POPULAR_PRODUCTS = 'SET_POPULAR_PRODUCTS';
const SET_PRODUCT = 'SET_PRODUCT';
const SET_VENDOR = 'SET_VENDOR';
const SET_EQUIPMENT_CATEGORIES = 'SET_EQUIPMENT_CATEGORIES';
const SET_EQUIPMENT_LOADED = 'SET_EQUIPMENT_LOADED';
const SET_PRODUCT_LINES = 'SET_PRODUCT_LINES';

export class ProductConnection extends SeamlessConnection<PublicProductDispatchers, ProductState> {
  constructor() {
    super(connectionName);
  }

  get initialState(): ProductState {
    return initialState;
  }

  getReducer() {
    return (state: ProductState, action: AnyAction): ProductState => {
      switch (action.type) {
        case this.getActionType(SET_LOADING_PRODUCT):
          return updateState(state, {
            loadingProduct: action.payload,
          });
        case this.getActionType(SET_POPULAR_PRODUCTS):
          return updateState(state, {
            popularProducts: action.payload,
          });
        case this.getActionType(SET_PRODUCT):
          return updateState(state, {
            entity: action.payload,
          });
        case this.getActionType(SET_VENDOR):
          return updateState(state, {
            vendor: action.payload,
          });
        case this.getActionType(SET_EQUIPMENT_CATEGORIES):
          return updateState(state, {
            equipmentCategories: action.payload,
          });
        case this.getActionType(SET_EQUIPMENT_LOADED):
          return updateState(state, {
            equipmentLoaded: action.payload,
          });
        case this.getActionType(SET_PRODUCT_LINES):
          return updateState(state, {
            lines: action.payload,
          });
        case this.getActionType(SET_ERROR):
          return {
            ...state,
            error: action.payload,
          };
        default:
          return state;
      }
    };
  }

  getPublicDispatchers() {
    return {
      setLoadingProducts: (code?: string) => (dispatch: Dispatch) => {
        if (code) {
          dispatch(this.getAction(SET_ERROR, undefined));
          dispatch(this.getAction(SET_LOADING_PRODUCT, code));
        } else {
          dispatch(this.getAction(SET_LOADING_PRODUCT, code));
        }
      },
      setEquipmentLoaded:
        (status = false) =>
        (dispatch: Dispatch) => {
          dispatch(this.getAction(SET_EQUIPMENT_LOADED, status));
        },
      setError: (error?: ErrorType) => (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_ERROR, error));
      },
      setPopularProducts: (products: Vehicle[]) => async (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_POPULAR_PRODUCTS, products));
      },
      setProduct: (product: ProductDetail) => async (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_PRODUCT, product));
      },
      setVendor: (vendor: Vendor) => async (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_VENDOR, vendor));
      },
      setEquipments: (equipments: EquipmentCategory[]) => async (dispatch: Dispatch) => {
        dispatch(this.getAction(SET_EQUIPMENT_CATEGORIES, adjustEnrichmentEquipments(equipments)));
        dispatch(this.getAction(SET_PRODUCT_LINES, getLines(equipments)));
      },
    };
  }
}

const setEnrichedData = async (productCode: string) => {
  if (infuse('config').pdp.vehicleEquipmentsSource !== 'enrichment') {
    return;
  }

  // Get the commission number from the product code
  const [, commissionNumber] = productCode.split('_');

  return productDispatchers.loadEnrichmentEquipments(commissionNumber);
};

const asyncProductDispatchers: AsyncProductDispatchers = {
  loadPopularProducts: async ({ category, fallbackCategory, filters, pageSize, sort }: any) => {
    try {
      // first clear the existing product items to avoid showing the old result set
      // when rendering the products on a different page
      productDispatchers.setPopularProducts([]);

      const productApi = infuse('productApi');
      let response = await productApi.searchProducts({
        selectedSort: sort || 'relevance',
        selectedCategory: category,
        filters,
        paging: {
          pageSize: pageSize || 5,
        } as IProductFilterPaginationDTO,
      });

      if (response.products.length === 0 && fallbackCategory) {
        response = await productApi.searchProducts({
          selectedSort: sort || 'relevance',
          selectedCategory: fallbackCategory,
          filters,
          paging: {
            pageSize: pageSize || 5,
          } as IProductFilterPaginationDTO,
        });
      }

      productDispatchers.setPopularProducts(response.products);
    } catch (error) {
      Logger.error('ProductConnection::loadProduct', error);
    }
  },
  loadProduct: async (productCode?: string) => {
    if (!productCode || typeof productCode !== 'string') {
      Promise.reject(new Error('loadProduct(): invalid productCode'));

      return;
    }

    try {
      productDispatchers.setLoadingProducts(productCode);

      const productApi = infuse('productExperienceApi');

      const [product] = await Promise.all([productApi.getProduct(productCode), setEnrichedData(productCode)]);

      productDispatchers.setProduct(product);

      if (product.vendor) {
        productDispatchers.setVendor(product.vendor);
      }
    } catch (error) {
      Logger.error('ProductConnection::loadProduct', error);
      productDispatchers.setError(ErrorType.NotFound);

      throw error;
    } finally {
      productDispatchers.setLoadingProducts();
    }
  },
  loadEnrichmentEquipments: async (commissionNumber: string) => {
    try {
      const configuration = infuse('config');

      if (!configuration.pdp.enrichmentApi) return;

      const { key, url } = configuration.pdp.enrichmentApi;

      const enrichmentApiConfiguration = new Configuration({
        apiKey: key,
        basePath: url,
      });

      const enrichmentApi = new EnrichmentApi(enrichmentApiConfiguration);

      productDispatchers.setEquipmentLoaded(false);

      const {
        data: { equipmentCategories },
      } = await enrichmentApi.getEnrichedStockVehicleUsingGET({
        commissionNumber,
        marketId: configuration.locale.replace('-', '_'),
      });

      productDispatchers.setEquipments(equipmentCategories as EquipmentCategory[]);
    } catch (error) {
      productDispatchers.setEquipments([]);
    } finally {
      productDispatchers.setEquipmentLoaded(true);
    }
  },
};

export let productStore: SeamlessCoreStore;
export let productDispatchers: ProductDispatchers;

export const initializeProductStore = async () => {
  productStore = (await initializeStoreConnection<ProductState, PublicProductDispatchers>(
    ProductConnection,
  )) as SeamlessCoreStore;
  productDispatchers = {
    ...(await productStore.getConnectionDispatchers<PublicProductDispatchers>(connectionName)),
    ...asyncProductDispatchers,
  };
};
